import { InvoiceGroup } from '@/api/useFreight/types'
import { defaultDateDef } from '@/components/_SiteWide/CellTemplates/typeColumnDefs'
import { formatters } from '@/utils/intl/dateTimeFormatters'
import { BBDTag, GraviButton, Horizontal, MaterialIcon, Texto, Vertical } from '@gravitate-js/excalibrr'
import {
  ValueGetterParams,
  ICellRendererParams,
  ValueFormatterParams,
  IFilterParams,
  ColDef,
  CellStyle,
} from 'ag-grid-community'
import dayjs from 'dayjs'
import { useNavigate } from 'react-router-dom'
import { TransactionErrorCellRenderer } from '../../FreightLineItems/components/renderers/OrderErrorCellRenderer'
import { InvoiceStatus } from '../Detail/components'
import { invoiceStatusConfig } from '../types'
import { getTimeDisplay } from '../useGridFilters'
import { Tooltip } from 'antd'
import { ArrowRightOutlined } from '@ant-design/icons'
import { UpdatedTag } from '../../shared'

export const invoiceGroupColDefs = (): ColDef<InvoiceGroup>[] => [
  {
    field: 'group_number',
    headerName: '',
    cellRenderer: 'agGroupCellRenderer',
    maxWidth: 50,
    filterValueGetter: (params: ValueGetterParams<InvoiceGroup>) => {
      return params.data?.invoices && params.data.invoices.length > 1 ? 'Has Rebill' : 'No Rebill'
    },
  },
  {
    headerName: '',
    field: 'book_type',
    maxWidth: 60,
    cellRenderer: (params: ICellRendererParams<InvoiceGroup>) => {
      const { value } = params

      if (!value) return null

      return value === 'Cost' ? (
        <Tooltip title='Cost'>
          <BBDTag
            style={{
              width: 'fit-content',
              display: 'flex',
              alignItems: 'center',
            }}
            error
          >
            <MaterialIcon size={15} icon='shadow_minus' />
          </BBDTag>
        </Tooltip>
      ) : (
        <Tooltip title='Revenue'>
          <BBDTag
            style={{
              width: 'fit-content',
              display: 'flex',
              alignItems: 'center',
            }}
            success
          >
            <MaterialIcon size={15} icon='payments' />
          </BBDTag>
        </Tooltip>
      )
    },
  },

  {
    headerName: 'Invoice',
    field: 'group_number',
    cellRenderer: (params: ICellRendererParams<InvoiceGroup>) => {
      // Check if this row has associated_invoices with count > 1
      const hasMultipleInvoices = params.data?.invoices && params.data.invoices.length > 1

      return (
        <Vertical style={{ overflow: 'visible' }} verticalCenter>
          <Horizontal verticalCenter style={{ overflow: 'visible', gap: 4 }}>
            {hasMultipleInvoices && (
              <Texto category='label' appearance='warning'>
                <MaterialIcon className='pt-2' size={14} icon='currency_exchange' />
              </Texto>
            )}
            <Texto textTransform='uppercase' weight='bold' appearance='default'>
              {params.value}
            </Texto>
          </Horizontal>

          <Texto category='label' appearance='hint'>
            {dayjs(params.data?.start_date || '').format('M/DD/YY')} -{' '}
            {dayjs(params.data?.end_date || '').format('M/DD/YY')}
          </Texto>
        </Vertical>
      )
    },
  },
  // add invoice_start invoice_end and time to invoice, a calculated column
  {
    headerName: 'Invoice Start',
    valueGetter: (params: ValueGetterParams<InvoiceGroup>) => dayjs(params.data?.start_date).format('M/DD/YY'),
    filter: 'agDateColumnFilter',
    initialHide: true,
  },
  {
    headerName: 'Invoice Date',
    valueGetter: (params: ValueGetterParams<InvoiceGroup>) => dayjs(params.data?.end_date).format('M/DD/YY'),
    filter: 'agDateColumnFilter',
    initialHide: true,
  },
  {
    headerName: 'Due Date',
    valueGetter: (params: ValueGetterParams<InvoiceGroup>) => dayjs(params.data?.due_date).format('M/DD/YY'),
    filter: 'agDateColumnFilter',
    initialHide: true,
  },
  {
    headerName: 'Time Until Sent',
    field: 'end_date',

    width: 150,
    sortable: true,
    ...defaultDateDef('end_date'),

    valueFormatter: (params: ValueFormatterParams<InvoiceGroup>) => {
      if (!params.value) return ''
      return getTimeDisplay(params.value).display
    },
  },
  {
    headerName: 'Sent Date',
    field: 'sent_date',
    width: 150,
    sortable: true,
    ...defaultDateDef('sent_date'),
  },
  {
    headerName: 'Updated',
    field: 'updated_date',
    filter: 'agDateColumnFilter',
    width: 140,
    minWidth: 140,
    cellRenderer: (params: ICellRendererParams<InvoiceGroup>) => {
      if (!params.data) return null

      return (
        <UpdatedTag
          created={{
            by: params.data.created_by ?? '',
            date: params.data.created_date,
          }}
          updated={{
            by: params.data.updated_by ?? 'System',
            date: params.data.updated_date,
          }}
          title='Invoice Updated'
        />
      )
    },
    filterParams: {
      browserDatePicker: true,
      minValidYear: 2000,
      maxValidYear: 2100,
      comparator: (filterDate: Date, cellValue: string) => {
        const cellDate = new Date(cellValue)
        if (filterDate.getTime() === cellDate.getTime()) return 0
        if (cellDate < filterDate) return -1
        if (cellDate > filterDate) return 1
        return 0
      },
    } as unknown as IFilterParams,
  },
  {
    headerName: 'Counterparty',
    field: 'counterparty_name',
    sort: 'asc',
    filter: true,
    filterParams: {
      buttons: ['reset'],
      debounceMs: 200,
      suppressAndOrCondition: true,
    } as unknown as IFilterParams,
    valueFormatter: (params: ValueFormatterParams<InvoiceGroup>) => {
      if (!params.value || params.value === 'Default Supplier Placeholder') {
        return 'N/A'
      }
      return params.value
    },
    minWidth: 180,
    enableValue: true,
    tooltipValueGetter: (params: { data?: InvoiceGroup }) => params.data?.counterparty_name || 'N/A',
  },
  {
    headerName: 'Status',
    field: 'status',
    filter: true,
    sortable: true,
    cellStyle: { display: 'flex', alignItems: 'flex-start' } as CellStyle,
    cellRenderer: (params: ICellRendererParams<InvoiceGroup>) => (
      <Horizontal verticalCenter horizontalCenter>
        <BBDTag
          success={invoiceStatusConfig[params.value as InvoiceStatus]?.tagProps.success}
          warning={invoiceStatusConfig[params.value as InvoiceStatus]?.tagProps.warning}
          error={invoiceStatusConfig[params.value as InvoiceStatus]?.tagProps.error}
          theme2={invoiceStatusConfig[params.value as InvoiceStatus]?.tagProps.theme2}
          style={{ width: 'fit-content', textTransform: 'capitalize' }}
        >
          <Horizontal verticalCenter horizontalCenter gap={8}>
            <MaterialIcon icon={invoiceStatusConfig[params.value as InvoiceStatus]?.icon || 'help'} />
            {invoiceStatusConfig[params.value as InvoiceStatus]?.title}
          </Horizontal>
        </BBDTag>
      </Horizontal>
    ),
    minWidth: 150,
  },
  {
    headerName: 'Errors',
    field: 'errors',
    valueGetter: (params: ValueGetterParams<InvoiceGroup>) => {
      return params.data?.errors?.map((err) => err.type) ?? null
    },
    cellRenderer: TransactionErrorCellRenderer,
  },
  {
    headerName: 'Error Messages',
    field: 'errors.messages', // fake field to hold error messages
    valueGetter: (params: ValueGetterParams<InvoiceGroup>) => {
      if (params.data.errors.length === 0) return null
      return params.data.errors.map((error) => error.message)
    },
    hide: true,
    suppressColumnsToolPanel: true, // added only for downloading purposes
  },
  {
    headerName: 'Orders',
    field: 'order_count',
    filter: 'agNumberColumnFilter',
    type: 'numericColumn',
    filterParams: {
      defaultOption: 'equals',
      buttons: ['apply', 'reset'],
      filterOptions: ['equals', 'notEqual', 'lessThan', 'lessThanOrEqual', 'greaterThan', 'greaterThanOrEqual'],
    } as unknown as IFilterParams,
  },
  {
    headerName: 'Exported',
    field: 'invoices',
    minWidth: 170,
    filter: 'agDateColumnFilter',
    valueGetter: (params: ValueGetterParams<InvoiceGroup>) => {
      const exportedDates = params.data?.invoices
        .map((invoice) => invoice.exported_at)
        .filter((date): date is string => !!date)

      if (exportedDates && exportedDates.length > 0) {
        return exportedDates.sort().reverse()[0] // Return the most recent date
      }
      return null
    },
    cellRenderer: (params: ICellRendererParams<string>) => {
      if (params.data?.status !== 'sent' || !params.value) return null
      return (
        <Horizontal style={{ overflow: 'visible' }} verticalCenter horizontalCenter gap={8}>
          <MaterialIcon style={{ color: 'var(--theme-success)' }} icon='check_circle' />
          {formatters.DATE_TIME(params.value)}
        </Horizontal>
      )
    },
  },
  {
    headerName: 'Base',
    field: 'base_amount',
    sortable: true,
    type: 'numericColumn',
    valueFormatter: (params: ValueFormatterParams<InvoiceGroup>) =>
      params.value?.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
      }),
    filter: 'agNumberColumnFilter',
    filterParams: {
      defaultOption: 'equals',
      buttons: ['apply', 'reset'],
    } as unknown as IFilterParams,
  },
  {
    headerName: 'Accessorials',
    field: 'accessorial_amount',
    sortable: true,
    type: 'numericColumn',
    valueFormatter: (params: ValueFormatterParams<InvoiceGroup>) =>
      params.value?.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
      }),
    filter: 'agNumberColumnFilter',
    filterParams: {
      defaultOption: 'equals',
      buttons: ['apply', 'reset'],
    } as unknown as IFilterParams,
  },
  {
    headerName: 'Surcharge',
    field: 'surcharge_amount',
    sortable: true,
    type: 'numericColumn',
    valueFormatter: (params: ValueFormatterParams<InvoiceGroup>) =>
      params.value?.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
      }),
    filter: 'agNumberColumnFilter',
    filterParams: {
      defaultOption: 'equals',
      buttons: ['apply', 'reset'],
    } as unknown as IFilterParams,
  },
  {
    headerName: 'Total',
    field: 'total_amount',
    sortable: true,
    type: 'numericColumn',
    cellStyle: { fontWeight: 'bold' } as CellStyle,
    valueFormatter: (params: ValueFormatterParams<InvoiceGroup>) => {
      if (!params.data) return ''

      const totalAmount = params.data.total_amount ?? 0
      return totalAmount.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
      })
    },
    filter: 'agNumberColumnFilter',
    filterParams: {
      defaultOption: 'equals',
      buttons: ['apply', 'reset'],
    } as unknown as IFilterParams,
  },
  {
    headerName: '',
    field: 'group_number',
    sortable: false,
    filter: false,
    width: 60,
    minWidth: 60,
    pinned: 'right',
    excludeFromExport: true,
    cellRenderer: (params: ICellRendererParams<InvoiceGroup>) => {
      const navigate = useNavigate()
      const { data } = params

      if (!data) return null
      if (!data.order_count || data.order_count === 0) return null

      return (
        <Horizontal gap={2}>
          <GraviButton
            icon={<ArrowRightOutlined />}
            onClick={(e) => {
              // Get all displayed rows from the grid API
              const displayedRows: InvoiceGroup[] = []
              const rowCount = params.api.getDisplayedRowCount()

              for (let i = 0; i < rowCount; i += 1) {
                const rowNode = params.api.getDisplayedRowAtIndex(i)
                if (rowNode?.data) {
                  displayedRows.push(rowNode.data)
                }
              }

              const currentIndex = displayedRows.findIndex((row) => row.id === data.id)

              // Navigate with full row data
              navigate(`/freight/invoices/${data.id}`, {
                state: {
                  displayedRows,
                  currentIndex,
                  currentRow: data,
                  filterContext: params.context,
                },
              })
            }}
            size='small'
          />
        </Horizontal>
      )
    },
  },
]
